import type { Link } from '@eneco/cms-types';

import type { FlowDialogContent } from '../types/content';

/** A step's `content` section as the Sitecore adapter still keeps it, whole, beside the fields it lifted out. */
interface KeptContentSection {
  moreInfoDialog?: FlowDialogContent;
}

/** The part of a step's content this reads: see `readMoreInfoDialogs`. */
export interface MoreInfoDialogFields {
  /**
   * The step's own dialog, on Sanity. On Sitecore this name holds the `moreInfoDialog` section instead,
   * so read it through `readMoreInfoDialogs` rather than directly.
   */
  moreInfoDialog?: FlowDialogContent;
  /**
   * The body, on Sanity: HTML, or `[]` when it is empty, since the Sanity adapter cannot tell empty rich
   * text from an empty list. On Sitecore the kept `content` section, which holds the step's own dialog.
   */
  content?: string | unknown[] | KeptContentSection;
  /** The `moreInfoDialog` section's title. `title` is also the `content` section's, so both CMSs prefix it. */
  moreInfoDialog_title?: string;
  text?: string;
  triggerText?: string;
  image?: unknown;
  buttonLink?: Link;
}

/** The `moreInfoDialog` section: a dialog with an image and a button. */
export interface SectionDialog {
  title?: string;
  text?: string;
  triggerText?: string;
  /** Read it with the CMS adapter's `toImage`: each CMS sends its own shape. */
  image?: unknown;
  /** Show it with `linkWithUrl`: an empty `href` means it leads nowhere. */
  buttonLink?: Link;
}

/**
 * Sitecore's kept section, as opposed to the body Sanity keeps under the same name. Only a plain object is
 * the section: an empty body arrives as `[]`, which is an object too.
 */
const isSection = (value: MoreInfoDialogFields['content']): value is KeptContentSection =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * A choice step's two more-info dialogs, from whichever CMS answered.
 *
 * The generic choice steps can carry two, both called `moreInfoDialog`: a `DialogField` inside the
 * `content` section, the step's own dialog, and a section of that name, a dialog with an image and a
 * button. Sanity keeps the field under its own name and lifts the section's fields out. The Sitecore
 * adapter lifts the section's fields out too, but it keeps the section itself under that name, which
 * leaves the field inside its own section (`liftFieldGroups` in `libs/cms-sitecore`). So the step's
 * own dialog is read from there when the `content` section is kept, as `readContentSection` reads the
 * body, and the section's fields are read by their own names on either lane.
 *
 * Delete this together with `readContentSection`, once the Sitecore adapter offers a section's
 * same-named member under that name.
 */
export const readMoreInfoDialogs = (
  fields: MoreInfoDialogFields | undefined,
): { fieldDialog?: FlowDialogContent; sectionDialog: SectionDialog } => {
  const content = fields?.content;
  return {
    fieldDialog: isSection(content) ? content.moreInfoDialog : fields?.moreInfoDialog,
    sectionDialog: {
      title: fields?.moreInfoDialog_title,
      text: fields?.text,
      triggerText: fields?.triggerText,
      image: fields?.image,
      buttonLink: fields?.buttonLink,
    },
  };
};
